import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { FadeUp } from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", email: "", phone: "", message: "" };

export function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.form.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm(EMPTY);
    } catch {
      const subject = encodeURIComponent(`Projektanfrage — ${form.name}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nUnternehmen: ${form.company}\nE-Mail: ${form.email}\nTelefon: ${form.phone}\n\nNachricht:\n${form.message}`
      );
      window.location.href = `mailto:info@ar-elektro.online?subject=${subject}&body=${body}`;
      toast.success(t.contact.form.fallback);
      setForm(EMPTY);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-border bg-white px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 outline-none transition-colors focus:border-brand";

  return (
    <section id="kontakt" data-testid="contact-section" className="bg-white py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          index={t.sections.contact.index}
          label={t.sections.contact.label}
          title={t.sections.contact.title}
          sub={t.sections.contact.sub}
        />

        <div className="grid gap-12 lg:grid-cols-5 lg:gap-20">
          <FadeUp className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between gap-10 bg-navy-deep p-8 md:p-10" data-testid="contact-info-panel">
              <div>
                <p className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-white/50">
                  ArStudio Elektro
                </p>
                <p className="font-display text-2xl font-bold leading-snug tracking-tight text-white md:text-3xl">
                  ar-elektro.online
                </p>
              </div>
              <div className="space-y-5">
                <a
                  href="tel:+38345813843"
                  data-testid="contact-phone-link"
                  className="group flex items-center gap-4 border border-white/15 p-5 transition-colors hover:border-brand"
                >
                  <Phone className="h-5 w-5 text-brand" />
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                      {t.contact.phoneLabel}
                    </span>
                    <span className="text-base font-medium text-white">+383 45 813 843</span>
                  </span>
                </a>
                <a
                  href="mailto:info@ar-elektro.online"
                  data-testid="contact-email-link"
                  className="group flex items-center gap-4 border border-white/15 p-5 transition-colors hover:border-brand"
                >
                  <Mail className="h-5 w-5 text-brand" />
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
                      {t.contact.emailLabel}
                    </span>
                    <span className="break-all text-base font-medium text-white">info@ar-elektro.online</span>
                  </span>
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className="mb-2 block text-sm font-medium text-navy">
                  {t.contact.form.name} *
                </label>
                <input id="cf-name" data-testid="contact-form-name" className={inputCls} value={form.name} onChange={set("name")} required />
              </div>
              <div>
                <label htmlFor="cf-company" className="mb-2 block text-sm font-medium text-navy">
                  {t.contact.form.company}
                </label>
                <input id="cf-company" data-testid="contact-form-company" className={inputCls} value={form.company} onChange={set("company")} />
              </div>
              <div>
                <label htmlFor="cf-email" className="mb-2 block text-sm font-medium text-navy">
                  {t.contact.form.email} *
                </label>
                <input id="cf-email" type="email" data-testid="contact-form-email" className={inputCls} value={form.email} onChange={set("email")} required />
              </div>
              <div>
                <label htmlFor="cf-phone" className="mb-2 block text-sm font-medium text-navy">
                  {t.contact.form.phone}
                </label>
                <input id="cf-phone" data-testid="contact-form-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="mb-2 block text-sm font-medium text-navy">
                  {t.contact.form.message} *
                </label>
                <textarea
                  id="cf-message"
                  rows={6}
                  data-testid="contact-form-message"
                  className={`${inputCls} resize-y`}
                  value={form.message}
                  onChange={set("message")}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="group inline-flex items-center gap-2 bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60"
                >
                  {sending ? t.contact.form.sending : t.contact.form.submit}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
